import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import { PictureAsPdf, KeyboardArrowRight, PhoneIphone, Search } from '@mui/icons-material';
import contractorService, { Contractor } from '../../services/contractorService';
import { getAllProjects, Project, getInitials } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../layouts/Layout';
import api from '../../services/api';
import { getSocket } from '../../services/realtimeService';
import { createTransferInvoice, CreateTransferInvoicePayload } from '../../services/invoicesService';

export interface ExpenseFormData {
  documentDate: string;
  receivedDate: string;
  projectId: string;
  contractorId?: string;
  contractorName: string;
  invoiceNumber: string;
  netAmount: number | '';
  vatRate: 0 | 8 | 23 | 'zw' | 'różne';
  grossAmount: number;
  paymentType: 'Karta' | 'Gotówka';
  remarks: string;
  file?: File | null;
  legalAcceptance: boolean;
  calcMode?: 'net' | 'gross' | 'none';
}

interface AddExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data: ExpenseFormData) => void;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

const AddExpenseDialog: React.FC<AddExpenseDialogProps> = ({ open, onClose, onSave }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [projects, setProjects] = React.useState<Array<{ project_id: string; project_name: string }>>([]);
  const STATIC_PROJECTS: Array<{ project_id: string; project_name: string }> = [
    { project_id: 'S14', project_name: 'SERWIS GWARANCYJNY: USŁUGI' },
    { project_id: 'S13', project_name: 'SERWIS GWARANCYJNY: MATERIAŁY' },
    { project_id: 'S12', project_name: 'LEASINGI' },
    { project_id: 'S11', project_name: 'OBSŁUGA PRAWNA, KSIĘGOWA, DORADCZA' },
    { project_id: 'S10', project_name: 'USŁUGI TELEINFORMATYCZNE' },
    { project_id: 'S9',  project_name: 'NAJEM MIESZKAN PRACOWNICZYCH' },
    { project_id: 'S8',  project_name: 'NAJEM POWIERZCHNI BIUROWEJ' },
    { project_id: 'S7',  project_name: 'PRACOWNICY B2B' },
    { project_id: 'S6',  project_name: 'ZARZĄD' },
    { project_id: 'S5',  project_name: 'USŁUGI WŁASNE' },
    { project_id: 'S4',  project_name: 'MATERIAŁY WŁASNE' },
    { project_id: 'S3',  project_name: 'DROBNE WYPOSAŻENIE - do 1500 zł' },
    { project_id: 'S2',  project_name: 'WYPOSAŻENIE - do 3.000 zł' },
    { project_id: 'S1',  project_name: 'ŚRODKI TRWAŁE - powyżej 3.000 zł' },
    { project_id: 'R11', project_name: 'PODATEK CIT' },
    { project_id: 'R10', project_name: 'PODATEK VAT' },
    { project_id: 'R9',  project_name: 'ZUS' },
    { project_id: 'R8',  project_name: 'PODATKI - PIT' },
    { project_id: 'R7',  project_name: 'POZOSTAŁE NIGDZIE NIE UJĘTE' },
    { project_id: 'R6',  project_name: 'OBSŁUGA BANKOWA' },
    { project_id: 'R5',  project_name: 'UBEZPIECZENIA I POLISY' },
    { project_id: 'R4',  project_name: 'PRACOWNICY UoP, UD, UZ' },
    { project_id: 'R3',  project_name: 'ZABEZPIECZENIE KONTRAKTÓW' },
    { project_id: 'R2',  project_name: 'KOSZTY KREDYTÓW' },
    { project_id: 'R1',  project_name: 'AMORTYZACJA' },
    { project_id: '.UN', project_name: 'USŁUGI NOCLEGOWE' },
    { project_id: '.UG', project_name: 'USŁUGI GASTRONOMICZNE' },
    { project_id: '.P2', project_name: 'POJAZDY - 50% ODLICZENIA VAT' },
    { project_id: '.P1', project_name: 'POJAZDY - 100% ODLICZENIA VAT' },
  ];
  const [loadingProjects, setLoadingProjects] = React.useState(false);

  const [contractorSearchQuery, setContractorSearchQuery] = React.useState('');
  const [contractorSearchResults, setContractorSearchResults] = React.useState<Contractor[]>([]);
  const [loadingContractors, setLoadingContractors] = React.useState(false);
  const [showContractorResults, setShowContractorResults] = React.useState(false);

  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const [showErrors, setShowErrors] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [form, setForm] = React.useState<ExpenseFormData>({
    documentDate: '',
    receivedDate: '',
    projectId: '',
    contractorId: undefined,
    contractorName: '',
    invoiceNumber: '',
    netAmount: '',
    vatRate: 23,
    grossAmount: 0,
    paymentType: 'Karta',
    remarks: '',
    file: null,
    legalAcceptance: false,
    calcMode: 'net',
  });
  const [scanWaiting, setScanWaiting] = React.useState(false);
  const [scanSessionId, setScanSessionId] = React.useState<string | null>(null);
  const scanHandlerRef = React.useRef<((p: any) => void) | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        setLoadingProjects(true);
        const all = await getAllProjects();
        const active = all.filter((p) => p.project_phase === 'W REALIZACJI')
          .map((p) => ({ project_id: p.project_id, project_name: p.project_name }));
        setProjects([...active, ...STATIC_PROJECTS]);
      } finally {
        setLoadingProjects(false);
      }
    };
    load();
  }, [open]);

  React.useEffect(() => {
    if (form.vatRate === 'różne') return;
    const vatPercent = form.vatRate === 'zw' ? 0 : Number(form.vatRate);
    if (form.calcMode === 'net') {
      const net = typeof form.netAmount === 'number' ? form.netAmount : 0;
      const gross = round2(net + net * (vatPercent / 100));
      setForm((prev) => ({ ...prev, grossAmount: gross }));
    } else {
      // calc from gross: keep grossAmount as entered, recompute net
      const gross = Number(form.grossAmount) || 0;
      const net = vatPercent === 0 ? gross : round2(gross / (1 + vatPercent / 100));
      setForm((prev) => ({ ...prev, netAmount: net }));
    }
  }, [form.netAmount, form.vatRate, form.grossAmount, form.calcMode]);

  const searchContractors = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setContractorSearchResults([]);
      setShowContractorResults(false);
      return;
    }
    try {
      setLoadingContractors(true);
      const response = await contractorService.filterContractors({ searchText: query }, 1, 10);
      setContractorSearchResults(response.contractors);
      setShowContractorResults(true);
    } catch (e) {
      setContractorSearchResults([]);
    } finally {
      setLoadingContractors(false);
    }
  };

  const handleContractorInputChange = (value: string) => {
    setContractorSearchQuery(value);
    setForm((prev) => ({ ...prev, contractorName: value, contractorId: undefined }));
    if (value.length >= 2) {
      searchContractors(value);
    } else {
      setContractorSearchResults([]);
      setShowContractorResults(false);
      if (value.length === 0) {
        setContractorSearchQuery('');
      }
    }
  };

  const handleSelectContractor = (contractor: Contractor) => {
    setForm((prev) => ({ ...prev, contractorId: contractor.id, contractorName: contractor.name }));
    setContractorSearchQuery(contractor.name);
    setShowContractorResults(false);
  };

  const onDropFile = (f: File) => {
    if (f && f.type === 'application/pdf') {
      if (fileUrl) URL.revokeObjectURL(fileUrl);
      const url = URL.createObjectURL(f);
      setFileUrl(url);
      setForm((prev) => ({ ...prev, file: f }));
    }
  };

  const canSave =
    !!form.documentDate &&
    !!form.receivedDate &&
    !!form.projectId &&
    !!form.contractorName &&
    (
      (form.vatRate === 'różne' && typeof form.netAmount === 'number' && form.netAmount > 0 && Number(form.grossAmount) > 0) ||
      (form.vatRate !== 'różne' &&
        (
          (form.calcMode === 'net' && typeof form.netAmount === 'number' && form.netAmount > 0) ||
          (form.calcMode === 'gross' && Number(form.grossAmount) > 0)
        )
      )
    ) &&
    !!form.file &&
    !!form.legalAcceptance;

  const resetState = () => {
    setForm({
      documentDate: '',
      receivedDate: '',
      projectId: '',
      contractorId: undefined,
      contractorName: '',
      invoiceNumber: '',
      netAmount: '',
      vatRate: 23,
      grossAmount: 0,
      paymentType: 'Karta',
      remarks: '',
      file: null,
      legalAcceptance: false,
      calcMode: 'net',
    });
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl(null);
    setContractorSearchQuery('');
    setContractorSearchResults([]);
    setShowContractorResults(false);
    // cleanup scan listener
    try {
      const s = getSocket();
      const h = scanHandlerRef.current;
      if (h) s.off('scan:completed', h);
    } catch {}
    scanHandlerRef.current = null;
    setScanWaiting(false);
    setScanSessionId(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSave = async () => {
    if (saving) return;
    if (!canSave || !form.file) { setShowErrors(true); return; }
    
    showNotification('Trwa przesyłanie PDF do Google Drive i zapisywanie w bazie...', 'info');
    setSaving(true);
    
    try {
      // Oblicz termin płatności (domyślnie 14 dni od daty dokumentu)
      const docDate = new Date(form.documentDate);
      const dueDate = new Date(docDate);
      dueDate.setDate(dueDate.getDate() + 14);
      
      const payload: CreateTransferInvoicePayload = {
        document_type: 'Gotówka',
        document_date: form.documentDate,
        received_date: form.receivedDate,
        project_id: form.projectId,
        contractor_id: form.contractorId || null,
        contractor_name: form.contractorName,
        net_amount: typeof form.netAmount === 'number' ? form.netAmount : 0,
        vat_rate: String(form.vatRate) as '0' | '8' | '23' | 'zw' | 'różne',
        gross_amount: form.grossAmount,
        calc_mode: form.calcMode || 'net',
        due_date: dueDate.toISOString().split('T')[0],
        priority: 'zwykły',
        comment: form.remarks || undefined,
        pdf: form.file,
      };
      
      await createTransferInvoice(payload, (user as any)?.google_access_token);
      showNotification('Wydatek został zapisany i pojawi się w zakładce "Faktury gotówkowe".', 'success');
      onSave?.(form);
      handleClose();
    } catch (e: any) {
      console.error('Nie udało się zapisać wydatku:', e);
      showNotification(e?.response?.data?.error || 'Nie udało się zapisać wydatku. Sprawdź konfigurację Google i spróbuj ponownie.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startPhoneScan = async () => {
    if (scanWaiting) return;
    setScanWaiting(true);
    try {
      const r = await api.post('/scan-requests', { type: 'expense' });
      const sid = r?.data?.data?.sessionId || null;
      if (!sid) throw new Error('Nie udało się utworzyć żądania skanu.');
      setScanSessionId(sid);
      const s = getSocket();
      const handler = async (payload: any) => {
        try {
          if (!payload || payload.sessionId !== sid || !payload.url) return;
          // pobierz PDF jako blob i podłóż do formularza (użyj origin backendu)
          const baseURL = (api as any).defaults?.baseURL || 'http://localhost:5000/api';
          const origin = baseURL.replace(/\/?api\/?$/, '');
          const absUrl = payload.url.startsWith('http') ? payload.url : `${origin}${payload.url}`;
          const resp = await fetch(absUrl);
          const blob = await resp.blob();
          const f = new File([blob], `scan-${Date.now()}.pdf`, { type: 'application/pdf' });
          onDropFile(f);
        } catch {}
        // posprzątaj
        try { s.off('scan:completed', handler); } catch {}
        scanHandlerRef.current = null;
        setScanWaiting(false);
        setScanSessionId(null);
      };
      scanHandlerRef.current = handler;
      s.on('scan:completed', handler);
    } catch (e) {
      setScanWaiting(false);
      setScanSessionId(null);
      showNotification('Nie udało się wysłać prośby o skan. Upewnij się, że aplikacja mobilna jest zainstalowana i zalogowana.', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { width: 'min(1400px, 96vw)' } }}>
      <DialogTitle sx={{ bgcolor: 'background.paper', borderBottom: '1px solid #e0e0e0', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'Użytkownik'}>
            <Avatar src={user?.avatar_url || undefined} sx={{ width: 28, height: 28 }}>
              {getInitials(user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}` || user?.email || 'U')}
            </Avatar>
          </Tooltip>
          <KeyboardArrowRight sx={{ color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Dodaj nowy wydatek
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2 }}>
          {/* Lewa kolumna – podgląd PDF (70%) */}
          <Box sx={{ width: { xs: '100%', md: '70%' } }}>
            <Box
              sx={{
                border: '2px dashed #9e9e9e',
                borderRadius: 1,
                height: { xs: '60vh', md: 'calc(88vh - 180px)' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: fileUrl ? 'background.paper' : 'transparent',
              }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) onDropFile(f);
              }}
            >
              {!fileUrl ? (
                <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                  <PictureAsPdf sx={{ fontSize: 56, color: 'text.disabled' }} />
                  <Typography variant="body1" sx={{ mt: 1 }}>Przeciągnij i upuść plik PDF tutaj</Typography>
                  <Typography variant="body2">lub</Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" component="label">
                      Wybierz plik PDF
                      <input type="file" hidden accept="application/pdf" onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onDropFile(f);
                      }} />
                    </Button>
                    <Button variant="outlined" onClick={startPhoneScan} startIcon={<PhoneIphone /> } disabled={scanWaiting}>
                      {scanWaiting ? 'Oczekiwanie…' : 'Skan z telefonu'}
                    </Button>
                  </Box>
                  {scanWaiting && (
                    <Box sx={{ mt: 1.5, display:'flex', alignItems:'center', gap:1, justifyContent:'center' }}>
                      <CircularProgress size={18} />
                      <Typography variant="caption" color="text.secondary">Sprawdź powiadomienie w aplikacji mobilnej</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <iframe title="expense-preview" src={fileUrl} style={{ width: '100%', height: '100%', border: 0 }} />
              )}
            </Box>
          </Box>

          {/* Prawa kolumna – formularz (30%) */}
          <Box sx={{ width: { xs: '100%', md: '30%' }, mt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                label="Data dokumentu"
                type="date"
                value={form.documentDate}
                onChange={(e) => setForm((p) => ({ ...p, documentDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
                error={showErrors && !form.documentDate}
                helperText={showErrors && !form.documentDate ? 'Wymagane' : ''}
              />
              <TextField
                label="Data otrzymania"
                type="date"
                value={form.receivedDate}
                onChange={(e) => setForm((p) => ({ ...p, receivedDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
                error={showErrors && !form.receivedDate}
                helperText={showErrors && !form.receivedDate ? 'Wymagane' : ''}
              />

              <Box sx={{ gridColumn: '1 / span 2' }}>
                <Autocomplete
                  size="small"
                  options={projects}
                  loading={loadingProjects}
                  getOptionLabel={(p) => `${p.project_id} ${p.project_name}`}
                  value={projects.find((p) => p.project_id === form.projectId) || null}
                  onChange={(_, val) => setForm((prev) => ({ ...prev, projectId: val ? String(val.project_id) : '' }))}
                  isOptionEqualToValue={(a, b) => a.project_id === b.project_id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Projekt"
                      error={showErrors && !form.projectId}
                      helperText={showErrors && !form.projectId ? 'Wymagane' : ''}
                    />
                  )}
                />
              </Box>
              <Box />

              <TextField
                fullWidth
                label="Nr faktury"
                value={form.invoiceNumber}
                onChange={(e) => setForm((p) => ({ ...p, invoiceNumber: e.target.value }))}
                size="small"
                sx={{ gridColumn: '1 / span 2' }}
              />

              {/* Pole Kontrahent z wyszukiwaniem */}
              <Box sx={{ gridColumn: '1 / span 2', position: 'relative' }}>
                <TextField
                  fullWidth
                  label="Kontrahent"
                  value={contractorSearchQuery !== '' ? contractorSearchQuery : form.contractorName}
                  onChange={(e) => handleContractorInputChange(e.target.value)}
                  onFocus={() => {
                    if (form.contractorName && contractorSearchQuery === '') {
                      setContractorSearchQuery(form.contractorName);
                    }
                  }}
                  size="small"
                  error={showErrors && !form.contractorName}
                  helperText={showErrors && !form.contractorName ? 'Wymagane' : ''}
                  InputProps={{
                    endAdornment: loadingContractors ? <CircularProgress size={18} /> : <Search />
                  }}
                />
                {showContractorResults && contractorSearchResults.length > 0 && (
                  <Paper elevation={3} sx={{ position: 'absolute', width: '100%', maxHeight: 220, overflow: 'auto', zIndex: 10, mt: 0.5 }}>
                    <List dense>
                      {contractorSearchResults.map((c) => (
                        <ListItem key={c.id} button onClick={() => handleSelectContractor(c)}>
                          <ListItemText primary={c.name} secondary={`${c.location}, NIP: ${c.nip}`} />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>

              <TextField
                label={
                  form.vatRate === 'różne'
                    ? 'Wartość netto'
                    : (form.calcMode === 'net' ? 'Wartość netto' : 'Wartość netto (obliczona)')
                }
                type="number"
                value={form.netAmount}
                onChange={(e) => {
                  const v = e.target.value === '' ? '' : Number(e.target.value);
                  setForm((p) => ({ ...p, netAmount: v }));
                }}
                size="small"
                inputProps={{ step: '0.01', min: 0 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                  readOnly: form.vatRate !== 'różne' && form.calcMode === 'gross'
                }}
                error={
                  showErrors && (
                    (form.vatRate === 'różne' && !(typeof form.netAmount === 'number' && form.netAmount > 0)) ||
                    (form.vatRate !== 'różne' && form.calcMode === 'net' && !(typeof form.netAmount === 'number' && form.netAmount > 0))
                  )
                }
                helperText={
                  showErrors && (
                    (form.vatRate === 'różne' && !(typeof form.netAmount === 'number' && form.netAmount > 0)) ||
                    (form.vatRate !== 'różne' && form.calcMode === 'net' && !(typeof form.netAmount === 'number' && form.netAmount > 0))
                  )
                    ? 'Wymagana wartość > 0'
                    : ''
                }
              />
              <FormControl size="small">
                <Select
                  value={form.vatRate}
                  onChange={(e) => {
                    const v = e.target.value as any;
                    setForm((p) => ({
                      ...p,
                      vatRate: v,
                      calcMode: v === 'różne' ? 'none' : (p.calcMode === 'none' ? 'net' : p.calcMode)
                    }));
                  }}
                >
                  <MenuItem value={23}>VAT 23%</MenuItem>
                  <MenuItem value={8}>VAT 8%</MenuItem>
                  <MenuItem value={0}>VAT 0%</MenuItem>
                  <MenuItem value={'zw'}>zw. (zwolnione)</MenuItem>
                  <MenuItem value={'różne'}>różne</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label={
                  form.vatRate === 'różne'
                    ? 'Wartość brutto'
                    : (form.calcMode === 'net' ? 'Wartość brutto (obliczona)' : 'Wartość brutto')
                }
                type="number"
                value={form.grossAmount}
                onChange={(e) => setForm((p) => ({ ...p, grossAmount: Number(e.target.value || 0) }))}
                size="small"
                inputProps={{ step: '0.01', min: 0 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                  readOnly: form.vatRate !== 'różne' && form.calcMode === 'net'
                }}
              />
              {/* Toggle netto/brutto */}
              <FormControl size="small">
                <Select
                  value={form.vatRate === 'różne' ? 'none' : form.calcMode}
                  onChange={(e) => setForm((p) => ({ ...p, calcMode: e.target.value as any }))}
                  disabled={form.vatRate === 'różne'}
                >
                  {form.vatRate === 'różne' ? (
                    <MenuItem value={'none'}>brak</MenuItem>
                  ) : (
                    <>
                      <MenuItem value={'net'}>Licz od netto</MenuItem>
                      <MenuItem value={'gross'}>Licz od brutto</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: '1 / span 2' }}>
                <Select
                  value={form.paymentType}
                  onChange={(e) => setForm((p) => ({ ...p, paymentType: e.target.value as 'Karta' | 'Gotówka' }))}
                  displayEmpty
                  renderValue={(v) => (v ? `Typ: ${v}` : 'Typ płatności')}
                >
                  <MenuItem value={'Karta'}>Typ: Karta</MenuItem>
                  <MenuItem value={'Gotówka'}>Typ: Gotówka</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Uwagi"
                value={form.remarks}
                onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
                size="small"
                multiline
                rows={3}
                sx={{ gridColumn: '1 / span 2' }}
              />
            </Box>
            {/* Nota prawna */}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                sx={{ alignItems: 'flex-start', m: 0 }}
                control={
                  <Checkbox
                    checked={form.legalAcceptance}
                    onChange={(e) => setForm((p) => ({ ...p, legalAcceptance: e.target.checked }))}
                    sx={{ mt: 0 }}
                  />
                }
                label={
                  <Typography variant="caption" component="p" sx={{ color: 'text.secondary', textAlign: 'justify', lineHeight: 1.4 }}>
                    Potwierdzam, że zgłaszany wydatek jest bezpośrednio związany z realizacją wybranego projektu i spełnia wymogi kwalifikowalności wydatków określone w OWU. Przyjmuję odpowiedzialność za rzetelność tego oświadczenia zgodnie z § 2.3.1 OWU.
                  </Typography>
                }
              />
            </Box>
            {/* Przyciski akcji wyrównane do prawej w kolumnie pól */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button onClick={handleClose} disabled={saving}>Anuluj</Button>
              <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}>{saving ? 'Zapisywanie...' : 'Zapisz'}</Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'none' }} />
    </Dialog>
  );
};

export default AddExpenseDialog;

